import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { UseFormReturn } from "react-hook-form";
import { FormData } from "../types";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "react-i18next";

interface BuyerInformationProps {
  form: UseFormReturn<FormData>;
}

export const BuyerInformation = ({ form }: BuyerInformationProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { data: templates } = useQuery({
    queryKey: ["recipient-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("recipient_templates")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const saveTemplateMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const templateData = {
        user_id: user.id,
        name: form.getValues("recipientName"),
        template_type: "business" as const,
        recipient_name: form.getValues("recipientName"),
        recipient_email: form.getValues("recipientEmail"),
        recipient_address: form.getValues("recipientAddress"),
        client_tax_id: form.getValues("clientTaxId"),
        client_phone: form.getValues("clientPhone"),
      };

      const { error } = await supabase
        .from("recipient_templates")
        .insert(templateData);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recipient-templates"] });
      toast.success("Recipient template saved successfully");
    },
    onError: (error) => {
      toast.error("Failed to save template", {
        description: error.message,
      });
    },
  });

  const handleTemplateChange = (templateId: string) => {
    const template = templates?.find((t) => t.id === templateId);
    if (!template) return;

    form.setValue("recipientName", template.recipient_name);
    form.setValue("recipientEmail", template.recipient_email);
    form.setValue("recipientAddress", template.recipient_address);
    form.setValue("clientTaxId", template.client_tax_id);
    form.setValue("clientPhone", template.client_phone);
  };

  return (
    <Card className="border-2 border-[#34375B]">
      <CardHeader className="flex flex-row items-center justify-between p-3 md:p-6">
        <CardTitle className="text-[#34375B] text-lg md:text-xl">{t("buyer_info.title", "Buyer Information")}</CardTitle>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="hover:bg-transparent p-0 text-[#9397A8] hover:text-[#34375B]"
          onClick={() => saveTemplateMutation.mutate()}
        >
          <Save className="h-4 w-4 transition-colors" />
        </Button>
      </CardHeader>
      <CardContent className="p-3 md:p-6 space-y-3 md:space-y-4">
        <div className="mb-4 md:mb-6 max-w-xs">
          <FormLabel className="text-sm md:text-base">{t("common.load_from_template", "Load from Template")}</FormLabel>
          <Select onValueChange={handleTemplateChange}>
            <SelectTrigger className="text-[#34375B] font-normal bg-white border border-gray-300 p-2 rounded-md text-sm md:text-base">
              <SelectValue placeholder={t("invoice_line_items.select_a_template", "Select a template")} />
            </SelectTrigger>
            <SelectContent className="bg-white border border-gray-300 p-2 rounded-md">
              {templates?.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  {template.recipient_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <FormField
            control={form.control}
            name="recipientName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm md:text-base">{t("buyer_info.client_name", "Client Name")}</FormLabel>
                <FormControl>
                  <Input placeholder="Lucky Luke" className="placeholder:text-[#9397A8] bg-white border border-gray-300 p-2 rounded-md text-sm md:text-base" {...field} />
                </FormControl>
                <FormMessage className="text-xs md:text-sm" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="clientTaxId"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm md:text-base">{t("buyer_info.tax_id", "Tax ID")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("buyer_info.client_tax_id", "Client Tax ID")} className="placeholder:text-[#9397A8] bg-white border border-gray-300 p-2 rounded-md text-sm md:text-base" {...field} />
                </FormControl>
                <FormMessage className="text-xs md:text-sm" />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <FormField
            control={form.control}
            name="recipientEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm md:text-base">{t("buyer_info.client_email", "Client Email")}</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="contact@gmail.com" className="placeholder:text-[#9397A8] bg-white border border-gray-300 p-2 rounded-md text-sm md:text-base" {...field} />
                </FormControl>
                <FormMessage className="text-xs md:text-sm" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="clientPhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm md:text-base">{t("buyer_info.client_phone", "Client Phone")}</FormLabel>
                <FormControl>
                  <Input placeholder="+1234567890" className="placeholder:text-[#9397A8] bg-white border border-gray-300 p-2 rounded-md text-sm md:text-base" {...field} />
                </FormControl>
                <FormMessage className="text-xs md:text-sm" />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="recipientAddress"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm md:text-base">{t("buyer_info.client_address", "Client Address")}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t("buyer_info.client_address_placeholder", "123 Street Name, City, Country")}
                  className="placeholder:text-[#9397A8] text-[#34375B] bg-white border border-gray-300 p-2 rounded-md text-sm md:text-base min-h-[80px]"
                  {...field}
                />
              </FormControl>
              <FormMessage className="text-xs md:text-sm" />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
};

